import { Button, message } from "antd";
import type { PurchaseContract } from "@zhihaoscm/entity";
import { PERMISSION, ROLE_CODE, SIGN_MODE } from "@zhihaoscm/meta";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { contractService } from "@/api/contractService";
import { fileService } from "@/api/fileService";
import { Breadcrumb, DetailHeader, InfoCard, ProTable, ZhBaseItem, ZhGrid, ZhMessageBox, type ProColumn } from "@/components/common";
import { AccountType, useAccountSession, useUserSession } from "@/stores/session";
import { PROJECT_CONTRACT_DATA_STATE, PROJECT_CONTRACT_STATE } from "../../../../../common/enum/contract";
import type { Contract } from "../../../../../common/types/contract";
import dayjs from "dayjs";
import "./console.css";

const signModes = SIGN_MODE.options.map(item => ({ label: item.label, value: Number(item.value) }));
const contractStates = PROJECT_CONTRACT_STATE.options.map(item => ({ label: item.label, value: Number(item.value) }));
const contractIdentity = (type: number | undefined, upper: boolean) =>
    type === 1
        ? upper
            ? "供应商"
            : "采购商"
        : type === 2
          ? upper
              ? ROLE_CODE.SUPPLY_CHAIN.intro
              : "采购商"
          : type === 3
            ? upper
                ? "供应商"
                : ROLE_CODE.SUPPLY_CHAIN.intro
            : "--";
/** 旧 BaseItem 的 file-id 行为：点击后从文件服务取得真实地址并预览。 */
function ContractFileLink({ fileId }: { fileId?: number }) {
    const [file, setFile] = useState<{ name: string; path: string }>();
    /** Vue BaseItem 会在 fileId 变化时立即取得文件名和下载地址。 */
    useEffect(() => {
        if (!fileId) { setFile(undefined); return; }
        let active = true;
        void fileService.findById(fileId).then(value => { if (active) setFile(value); }).catch(() => undefined);
        return () => { active = false; };
    }, [fileId]);
    return fileId ? (
        <span className="contract-file-value">{file?.name}<a className="contract-file-link" href={file?.path} target="_blank" rel="noreferrer">查看/下载</a></span>
    ) : (
        <>--</>
    );
}

/** 合同列表保留旧版项目/编号搜索、签署方式和状态筛选。 */
export function ContractListPage() {
    const navigate = useNavigate();
    const roleCode = useUserSession(state => state.roleCode) ?? ROLE_CODE.CUSTOMER.code;
    const title = `${roleCode === ROLE_CODE.SUPPLY_CHAIN.code ? "联合采购" : roleCode === ROLE_CODE.SUPPLIER.code ? "客户" : "采购"}合同列表`;
    const columns = useMemo<ProColumn<Contract>[]>(
        () => [
            {
                key: "id",
                title: "编号",
                width: 170,
                search: { key: "searchParam", type: "input", placeholder: "请输入项目名称或合同编号", width: 120, span: 5 },
                render: (_, row) => (
                    <Button
                        className="contract-code-link"
                        type="link"
                        onClick={() => navigate(`/console/contract/detail?id=${row.id}`)}
                    >
                        {row.id}
                    </Button>
                )
            },
            { key: "projectName", title: "项目", minWidth: 142 },
            {
                key: "buyerCustomerId",
                title: "买方",
                minWidth: 142,
                render: (_, row) =>
                    row.buyerEnterprise?.name ||
                    row.buyerEnterprise?.legalRepresentative ||
                    row.buyerEnterprise?.realName ||
                    "--"
            },
            { key: "sellerCustomerId", title: "卖方", minWidth: 142, render: (_, row) => row.sellerEnterprise?.name ?? "--" },
            {
                key: "signMode",
                title: "签署方式",
                width: 114,
                enum: signModes,
                search: { key: "signMode", type: "select", placeholder: "请选择签署方式", options: signModes, width: 80, span: 3 }
            },
            {
                key: "updatedTime",
                title: "更新时间",
                width: 156,
                render: (_, row) => (row.updatedTime ? dayjs(row.updatedTime).format("YYYY-MM-DD HH:mm:ss") : "--")
            },
            {
                key: "state",
                title: "状态",
                width: 114,
                search: { key: "state", type: "select", placeholder: "请选择状态", options: contractStates, width: 80, span: 3 },
                render: (_, row) => PROJECT_CONTRACT_STATE.getIntroByCode(Number(row.state) >> 16) ?? "--"
            }
        ],
        [navigate]
    );
    return (
        <section className="contract-list-page section-card">
            <Breadcrumb routes={[{ name: "采购" }, { name: "合同" }]} />
            <div className="contract-list-table">
                <ProTable
                    title={title}
                    rowKey="id"
                    columns={columns}
                    request={params => contractService.paging(params as never)}
                />
            </div>
        </section>
    );
}

/** 合同详情逐项对应旧 detail/index.vue，并保留印章、权限与本人签署状态门禁。 */
export function ContractDetailPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const id = params.get("id") ?? "";
    const user = useUserSession();
    const account = useAccountSession();
    const [info, setInfo] = useState<PurchaseContract>();
    const [invalidOpen, setInvalidOpen] = useState(false);
    const load = async () => {
        if (!id) return;
        try { setInfo(await contractService.getInfo(id)); } catch { /* Vue 详情不插入额外错误页。 */ }
    };
    useEffect(() => {
        void load();
    }, [id]);
    /** 打开服务端返回的签署凭证；无凭证代表已转交印章使用人签署。 */
    const openTicket = async (action: () => Promise<{ ticket?: string; errors: unknown[]; status: number }>) => {
        const result = await action();
        if (result.errors?.length || result.status === 400) return;
        if (!result.ticket) {
            void message.success("已发起签署，请等待专人进行签署");
            return;
        }
        await load();
        window.open(result.ticket);
    };
    if (!info) return null;
    const proxyId = account.proxyActualAccount?.id || user.userInfo.customer?.id;
    const actualId = account.actualAccount?.id || user.userInfo.customer?.id;
    const isBuyer = String(proxyId) === String(info.buyerCustomerId);
    const signSelf = isBuyer
        ? info.buyerSignState
        : String(proxyId) === String(info.sellerCustomerId)
          ? info.sellerSignState
          : undefined;
    const sealId = isBuyer ? (info.buyerEnterprise as any)?.signId : (info.sellerEnterprise as any)?.signId;
    const isSealUser = String(actualId) === String(sealId);
    const permissions = (user.userInfo as any).permissions as number[] | undefined;
    const canInvalidate =
        account.accountType !== AccountType.SUB_ACCOUNT || Boolean(permissions?.includes(PERMISSION.CONTRACT.code));
    const invalidState = Number((info as any).invalidSignState ?? 0);
    const invalidSigned = isBuyer ? invalidState === 1 || invalidState === 3 : invalidState === 2 || invalidState === 3;
    const title = `${user.roleCode === ROLE_CODE.SUPPLY_CHAIN.code ? "联合采购" : user.roleCode === ROLE_CODE.SUPPLIER.code ? "客户" : "采购"}合同详情`;
    const buyerName =
        (info.buyerEnterprise as any)?.name ||
        (info.buyerEnterprise as any)?.legalRepresentative ||
        (info.buyerEnterprise as any)?.realName ||
        "--";
    const sellerName = info.sellerEnterprise?.name || "--";
    const dateRange =
        info.startDate && info.finishDate
            ? `${dayjs(info.startDate).format("YYYY-MM-DD")} 至 ${dayjs(info.finishDate).format("YYYY-MM-DD")}`
            : "--";
    return (
        <section className="contract-detail-page detail-container">
            <DetailHeader
                title={title}
                rightInfos={[
                    {
                        label: "创建时间",
                        value: info.createdTime ? dayjs(info.createdTime).format("YYYY-MM-DD HH:mm:ss") : "--"
                    }
                ]}
                state={{ text: PROJECT_CONTRACT_DATA_STATE.getIntroByCode(info.state) ?? undefined, color: [4, 6].includes(info.state) ? "#999999" : "#3370ff" }}
                menu={
                    <Breadcrumb
                        routes={[
                            { name: "采购" },
                            { name: "合同", path: "/console/contract" },
                            { name: "合同详情" }
                        ]}
                    />
                }
            />
            <InfoCard title="基本信息">
                <ZhGrid cols={5} gap={16}>
                    <ZhBaseItem label="项目" span={2}>
                        <button
                            className="contract-project-link"
                            onClick={() => info.projectId && navigate(`/console/project/detail?id=${info.projectId}`)}
                            type="button"
                        >
                            {info.projectName || "--"}
                        </button>
                    </ZhBaseItem>
                    <ZhBaseItem label="名称" span={2} value={info.name || "--"} />
                    <ZhBaseItem label="签署方式" value={SIGN_MODE.getIntroByCode(info.signMode) || "--"} />
                    <ZhBaseItem label="买方" span={2}>
                        <>
                            {info.buyerSignState === PROJECT_CONTRACT_STATE.SIGNING_IN_PROGRESS.code ? (
                                <i className="iconfont icon-Frame-1 contract-sign-icon" />
                            ) : null}
                            {buyerName}
                            <span className="contract-identity">{contractIdentity(info.type, false)}</span>
                        </>
                    </ZhBaseItem>
                    <ZhBaseItem label="卖方" span={2}>
                        <>
                            {info.sellerSignState === PROJECT_CONTRACT_STATE.SIGNING_IN_PROGRESS.code ? (
                                <i className="iconfont icon-Frame-1 contract-sign-icon" />
                            ) : null}
                            {sellerName}
                            <span className="contract-identity">{contractIdentity(info.type, true)}</span>
                        </>
                    </ZhBaseItem>
                    <ZhBaseItem label="合同文件">
                        <ContractFileLink fileId={info.fileId} />
                    </ZhBaseItem>
                    <ZhBaseItem label="起止日期" span={2} value={dateRange} />
                    <ZhBaseItem
                        label="签订日期"
                        span={2}
                        value={info.completionTime ? dayjs(info.completionTime).format("YYYY-MM-DD") : "--"}
                    />
                </ZhGrid>
            </InfoCard>
            {info.state === PROJECT_CONTRACT_STATE.INVALIDING.code ||
            info.state === PROJECT_CONTRACT_STATE.INVALID.code ? (
                <InfoCard title="作废信息">
                    <ZhGrid cols={5} gap={16}>
                        <ZhBaseItem label="文件">
                            <ContractFileLink fileId={info.invalidFileId} />
                        </ZhBaseItem>
                        {info.state === PROJECT_CONTRACT_STATE.INVALID.code ? (
                            <ZhBaseItem
                                label="时间"
                                value={info.invalidTime ? dayjs(info.invalidTime).format("YYYY-MM-DD HH:mm:ss") : "--"}
                            />
                        ) : null}
                    </ZhGrid>
                </InfoCard>
            ) : null}
            <div className="contract-detail-actions">
                {info.signMode === SIGN_MODE.ONLINE.code &&
                info.state === PROJECT_CONTRACT_STATE.COMPLETED.code &&
                canInvalidate ? (
                    <Button type="primary" onClick={() => setInvalidOpen(true)}>
                        发起作废
                    </Button>
                ) : null}
                {info.signMode === SIGN_MODE.ONLINE.code &&
                info.state === PROJECT_CONTRACT_STATE.INVALIDING.code &&
                isSealUser &&
                !invalidSigned ? (
                    <Button type="primary" onClick={() => void openTicket(() => contractService.sign(id))}>
                        签署作废文件
                    </Button>
                ) : null}
                {Number(signSelf) < 2 && isSealUser ? (
                    <Button type="primary" onClick={() => void openTicket(() => contractService.sign(id))}>
                        签署
                    </Button>
                ) : null}
                <Button type="primary" onClick={() => navigate(-1)}>
                    返回
                </Button>
            </div>
            <ZhMessageBox
                open={invalidOpen}
                tip="作废后，将发起作废合同签署，需双方签署后该合同才作废，是否确认发起？"
                onCancel={() => setInvalidOpen(false)}
                onSubmit={async () => {
                    await openTicket(() => contractService.startInvalid(Number(id)));
                    void message.success("已发起作废");
                    setInvalidOpen(false);
                }}
            />
        </section>
    );
}
